import { useEffect, useRef, useState } from "react"

type Options = {
    intervalMs?: number
    maxDurationMs?: number
}

const DEFAULT_INTERVAL_MS = 3000
const DEFAULT_MAX_DURATION_MS = 30000

// Refresca periódicamente los comprobantes pendientes de autorización y se detiene solo.
// - Corre mientras haya ids pendientes, cada `intervalMs` (con una primera consulta inmediata).
// - Al superar `maxDurationMs` deja de pollear y marca `isExhausted` para que la UI ofrezca el refresh manual.
// - Si cambia el conjunto de pendientes (p. ej. una reemisión) reinicia el contador.
export const useFiscalDocumentAutoRefresh = (
    pendingIds: string[],
    refresh: (pendingIds: string[]) => Promise<void>,
    options: Options = {}
) => {
    const { intervalMs = DEFAULT_INTERVAL_MS, maxDurationMs = DEFAULT_MAX_DURATION_MS } = options

    const [isExhausted, setIsExhausted] = useState(false)

    const refreshRef = useRef(refresh)
    refreshRef.current = refresh

    const pendingIdsRef = useRef(pendingIds)
    pendingIdsRef.current = pendingIds

    // Identidad estable del conjunto de pendientes: dispara el ciclo y lo reinicia cuando cambia.
    const pendingKey = [...pendingIds].sort().join('|')

    useEffect(() => {
        if (!pendingKey) {
            setIsExhausted(false)
            return
        }

        let cancelled = false
        let inFlight = false
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const startedAt = Date.now()

        const tick = async () => {
            if (cancelled) return

            if (Date.now() - startedAt >= maxDurationMs) {
                setIsExhausted(true)
                return
            }

            const ids = pendingIdsRef.current
            if (ids.length === 0) return

            if (!inFlight) {
                inFlight = true
                try {
                    await refreshRef.current(ids)
                } catch {
                    // Un tick fallido (red, server) no corta el ciclo: se reintenta hasta agotar el tiempo.
                } finally {
                    inFlight = false
                }
            }

            if (!cancelled) {
                timeoutId = setTimeout(tick, intervalMs)
            }
        }

        setIsExhausted(false)
        tick()

        return () => {
            cancelled = true
            if (timeoutId) clearTimeout(timeoutId)
        }
    }, [pendingKey, intervalMs, maxDurationMs])

    return { isExhausted }
}
